'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useAuth } from '@/contexts/auth-context'
import { cn } from '@/lib/utils'
import { UserNav } from '@/components/layout/user-nav'
import { SettingsMenu } from '@/components/layout/settings-menu'
import { Menu } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useState } from 'react'
import { SideMenu } from '@/components/layout/side-menu'
import { AccessLevel } from '@/contexts/auth-context'

// ─── Матрица видимости пунктов меню ──────────────────────────────────────────
//
// Каждый пункт задаёт массив roles — точные роли, которые его видят.
// Это исключает путаницу иерархической проверки (>=) и позволяет в одном
// месте видеть и контролировать меню каждой роли.
//
// Роли (по возрастанию уровня):
//   engineer | senior-engineer | implementation-head | department-head
//   project-manager | hr | admin

interface NavItem {
  title: string
  href: string
  roles: AccessLevel[]  // точный список ролей, которым пункт виден
}

// Порядок пунктов меню для администратора (по href)
const ADMIN_NAV_ORDER = ['/news', '/projects', '/tasks', '/timesheets', '/reports', '/users']

const navigation: NavItem[] = [
  {
    title: 'Новости',
    href: '/news',
    roles: [
      'engineer',
      'senior-engineer',
      'implementation-head',
      'department-head',
      'project-manager',
      'hr',
      'admin',
    ],
  },
  {
    title: 'Проекты',
    href: '/projects',
    roles: [
      'engineer',
      'senior-engineer',
      'implementation-head',
      'department-head',
      'project-manager',
      'admin',
    ],
  },
  {
    title: 'Табели',
    href: '/timesheets',
    roles: [
      'engineer',
      'senior-engineer',
      'implementation-head',
      'department-head',
      'project-manager',
      'hr',
      'admin',
    ],
  },
  {
    title: 'Сотрудники',
    href: '/users',
    roles: [
      'senior-engineer',
      'implementation-head',
      'department-head',
      'project-manager',
      'hr',
      'admin',
    ],
  },
  {
    title: 'Задачи',
    href: '/tasks',
    roles: [
      'admin',
    ],
  },
  {
    title: 'Отчёты',
    href: '/reports',
    roles: [
      'admin',
    ],
  },
]

export function Navigation() {
  const pathname = usePathname()
  const { user } = useAuth()
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false)

  if (!user) return null

  // Фильтрация по точному совпадению роли — никакой иерархии
  const visibleNavigation = navigation
    .filter(item => item.roles.includes(user.accessLevel as AccessLevel))
    // Для администратора — сортировка по заданному порядку
    .sort((a, b) => {
      if (user.accessLevel === 'admin') {
        return ADMIN_NAV_ORDER.indexOf(a.href) - ADMIN_NAV_ORDER.indexOf(b.href)
      }
      return 0
    })

  return (
    <div className="flex items-center gap-2">
      {/* Мобильное меню — кнопка-гамбургер */}
      <div className="lg:hidden">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsSideMenuOpen(true)}
          className="h-8 w-8 mr-0.5"
        >
          <Menu className="h-4 w-4" />
        </Button>

        {/* Side Menu for mobile */}
        <SideMenu
          isOpen={isSideMenuOpen}
          onClose={() => setIsSideMenuOpen(false)}
          navigation={visibleNavigation}
          currentPath={pathname}
        />
      </div>

      {/* Десктопная навигация */}
      <div className="hidden lg:flex items-center gap-4 mr-4">
        {visibleNavigation.map((item) => (
          <Link
            key={item.href}
            href={item.href}
            className={cn(
              'text-sm transition-colors hover:text-primary',
              pathname === item.href || pathname.startsWith(item.href + '/')
                ? 'text-primary font-medium'
                : 'text-muted-foreground'
            )}
          >
            {item.title}
          </Link>
        ))}
      </div>

      {/* Управление пользователем */}
      <div className="flex items-center gap-1.5">
        <SettingsMenu />
        <UserNav />
      </div>
    </div>
  )
}
